import { useState } from "react";
import { api, handleFeedback, type FeedbackItem } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";

/**
 * Operator feedback inbox — its OWN page (Operations rail). In-app notes left by signed-in users from
 * the floating widget (POST /feedback); each carries the page it was sent from + the submitter's
 * email/workspace, so an operator sees who + where. New notes sort first; "Mark handled" clears one from
 * the new count. (Lived under Maintenance before — it didn't belong there.)
 */
export function Feedback() {
  const { data, loading, reload } = useAsync<{ items: FeedbackItem[]; newCount: number }>(
    () => api("/settings/feedback"),
    [],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const items = data?.items ?? [];
  const newCount = data?.newCount ?? 0;

  async function markHandled(id: string) {
    setBusy(id);
    setErr(null);
    try {
      await handleFeedback(id);
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : "failed to update");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <div className="section-head">
        <div>
          <h2>
            Feedback{newCount > 0 ? <span className="count" style={{ marginLeft: 8 }}>{newCount} new</span> : null}
          </h2>
          <p className="fleet-line" style={{ marginTop: 6 }}>
            Notes left by users from the in-app Feedback button. Each shows the page it came from.
          </p>
        </div>
      </div>
      {err && <div className="err mt">{err}</div>}
      {loading && !data ? (
        <div className="muted pad">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">No feedback yet.</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>From</th>
                <th>Message</th>
                <th>Page</th>
                <th>Sent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className={f.status === "handled" ? "muted" : undefined}>
                  <td>
                    {f.email || "—"}
                    {f.workspaceName ? <div className="muted">{f.workspaceName}</div> : null}
                  </td>
                  <td style={{ whiteSpace: "pre-wrap", maxWidth: 360 }}>{f.message}</td>
                  <td className="muted nowrap">{f.page || "—"}</td>
                  <td className="nowrap">{fmt(f.createdAt)}</td>
                  <td className="acts">
                    {f.status === "new" ? (
                      <button className="btn ghost sm" disabled={busy !== null} onClick={() => void markHandled(f.id)}>
                        {busy === f.id ? "…" : "Mark handled"}
                      </button>
                    ) : (
                      <span className="muted">handled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
